"use client"
import { useTheme, useMediaQuery } from "@mui/material"
import { useSelector } from "react-redux"
import { makeStyles } from "tss-react/mui"
import { useState, useEffect } from "react"
import Logo from "../resources/images/logo.svg?react"

const useStyles = makeStyles()((theme) => ({
  image: {
    alignSelf: "center",
    maxWidth: "300px",
    maxHeight: "150px",
    width: "auto",
    height: "auto",
    margin: theme.spacing(2),
  },
}))

const normalizePath = (path) => {
  if (!path) return ""
  if (path.startsWith("http") || path.startsWith("/")) {
    return path
  }
  return `/${path}`
}

const checkImageExists = async (url) => {
  if (!url) return false;
  try {
    const response = await fetch(normalizePath(url), {
      method: 'HEAD',
      // Timeout para evitar espera longa por arquivos que não existem
      signal: AbortSignal.timeout(3000)
    });
    return response.ok;
  } catch {
    return false;
  }
};

const getInvertedLogoPaths = (logoPath) => {
  if (!logoPath) return [];

  const paths = [];

  // Primeiro tenta os caminhos no sistema de arquivos
  if (logoPath.startsWith('/api/revendas/logo/')) {
    const revendaCode = logoPath.split('/').pop().split('?')[0];
    paths.push(`/themes/${revendaCode}/logoInverted.svg`);
    paths.push(`/themes/${revendaCode}/logoInverted.png`);
    paths.push(`/themes/${revendaCode}/logoInverted.jpg`);
    paths.push(`/themes/${revendaCode}/logoInverted.webp`);
  }

  // Depois tenta o parâmetro de query (fallback)
  const separator = logoPath.includes('?') ? '&' : '?';
  paths.push(`${logoPath}${separator}inverted=true`);

  return paths;
}

const LogoImage = ({ color, alt, onLoad, style }) => {
  const theme = useTheme()
  const { classes } = useStyles()
  const expanded = !useMediaQuery(theme.breakpoints.down("lg"))

  const revenda = useSelector((state) => state.session.revenda)
  const serverLogo = useSelector((state) => state.session.server.attributes?.logo)
  const serverLogoInverted = useSelector((state) => state.session.server.attributes?.logoInverted)

  const [logoError, setLogoError] = useState(false)
  const [invertedLogoError, setInvertedLogoError] = useState(false)
  const [availableLogos, setAvailableLogos] = useState({
    normal: null,
    inverted: null
  })
  const isDarkMode = theme.palette.mode === 'dark'

  useEffect(() => {
    const checkAvailableLogos = async () => {
      if (revenda?.logo) {
        try {
          const normalExists = await checkImageExists(revenda.logo);

          let invertedExists = false;
          let invertedLogoUrl = null;

          // Só verifica logos invertidos se estiver no modo escuro
          if (isDarkMode) {
            const invertedPaths = getInvertedLogoPaths(revenda.logo);
            for (const path of invertedPaths) {
              const exists = await checkImageExists(path);
              if (exists) {
                invertedExists = true;
                invertedLogoUrl = path;
                break;
              }
            }
          }

          setAvailableLogos({
            normal: normalExists ? revenda.logo : null,
            inverted: invertedExists ? invertedLogoUrl : null
          });
        } catch (error) {
          console.warn('Erro ao verificar logos:', error);
          setAvailableLogos({
            normal: revenda.logo,
            inverted: null
          });
        }
      }
    };

    checkAvailableLogos();
  }, [revenda?.logo, isDarkMode]);

  const imgProps = {
    className: classes.image,
    alt: alt || revenda?.title || "Logomarca",
    onLoad,
    style: { ...style, objectFit: 'contain' }
  }

  const handleImageError = (e) => {
    console.warn('Erro ao carregar imagem:', e.target.src);
    if (e.target.src.includes('inverted=true') || e.target.src.includes('logoInverted')) {
      setInvertedLogoError(true);
    } else {
      setLogoError(true);
    }
  }

  const getValidLogoUrl = (url) => {
    if (!url) return null;
    try {
      // Verifica se é uma URL válida
      if (url.startsWith('http') || url.startsWith('/')) {
        return url;
      }
      return `/${url}`;
    } catch {
      return null;
    }
  }

  const getLogoUrl = () => {
    // Primeiro tenta a revenda com fallback para modo escuro
    if (availableLogos.normal && !logoError) {
      if (isDarkMode && availableLogos.inverted && !invertedLogoError) {
        return getValidLogoUrl(availableLogos.inverted);
      }
      return getValidLogoUrl(availableLogos.normal);
    }

    // Fallback para logo do servidor
    if (serverLogo) {
      if (isDarkMode && serverLogoInverted && !invertedLogoError) {
        return getValidLogoUrl(serverLogoInverted);
      }
      return getValidLogoUrl(serverLogo);
    }

    return null;
  }

  const src = getLogoUrl();

  if (src) {
    return (
      <img
        {...imgProps}
        src={src}
        onError={handleImageError}
        loading="lazy"
      />
    )
  }

  // Fallback final: SVG padrão
  return <Logo {...imgProps} style={{ ...style, color }} />
}

export default LogoImage